import { compareByFixType } from "@/helpers/compare.helper"

import type { BaseImageFinding, BaseImageFix } from "@/domain/issue"

export const findingsToBaseImageSources = (findings: BaseImageFinding[]) => {
  // grouped findings by base image digest
  const grouped = findings.reduce(
    (acc, curr) => {
      const baseImages = curr.introducedThrough
        .map((introducedTrough) => introducedTrough.baseImageDetails)
        .filter((baseImageDetails) => baseImageDetails != null)
        .sort((a, b) => b.baseImageLayerIndex - a.baseImageLayerIndex)

      if (baseImages.length > 0) {
        const baseImage = baseImages[0]
        const digest = baseImage.baseImageDigest

        if (!acc[digest]) {
          acc[digest] = {
            tag: "",
            remedies: []
          }
        }

        if (acc[digest].tag === "" && baseImage.baseImageNames.length > 0) {
          acc[digest].tag =
            baseImage.baseImageNames.find(
              (baseImageName) => baseImageName !== ""
            ) || ""
        }

        acc[digest].remedies = [
          ...acc[digest].remedies,
          ...(curr.remedies || [])
        ]
      }

      return acc
    },
    {} as Record<
      string,
      {
        tag: string
        remedies: BaseImageFix[]
      }
    >
  )

  const sources = Object.values(grouped)
    .filter((val) => val.remedies.length > 0)
    .map((val) => {
      let tag = val.tag
      const fixWithCurrent = val.remedies.find((fix) => fix.currentUrl !== "")

      if (
        fixWithCurrent &&
        fixWithCurrent.currentUrl !== "" &&
        (tag === "" || tag !== fixWithCurrent.currentUrl)
      ) {
        tag = fixWithCurrent.currentUrl
      }

      return {
        tag: tag,
        remedies: getUniqueBaseImageFixes(val.remedies)
      }
    })
    .sort((a, b) => {
      const fixTypePriority = compareByFixType(a.remedies[0], b.remedies[0])
      if (fixTypePriority !== 0) return fixTypePriority

      const aTag = a.tag || ""
      const bTag = b.tag || ""
      return aTag.localeCompare(bTag)
    })

  return sources
}

const getUniqueBaseImageFixes = (fixes: BaseImageFix[]): BaseImageFix[] => {
  const uniqueFixes: BaseImageFix[] = []

  fixes?.forEach((fix) => {
    const existingIndex = uniqueFixes.findIndex(
      (uniqueFix: BaseImageFix) =>
        uniqueFix.digest + uniqueFix.repository === fix.digest + fix.repository
    )

    if (existingIndex === -1) {
      uniqueFixes.push(fix)
    } else {
      const existingFix = uniqueFixes[existingIndex]
      const mergedFix: BaseImageFix = {
        digest: existingFix.digest,
        repository: existingFix.repository,
        tags: existingFix.tags,
        fixType: existingFix.fixType,
        currentUrl: existingFix.currentUrl
      }

      // if the new fix has better fix type we want it's tags to be first
      if (compareByFixType(fix, existingFix) < 0) {
        mergedFix.fixType = fix.fixType
        mergedFix.currentUrl = fix.currentUrl

        const newTagsSet = new Set(fix.tags)
        existingFix.tags.forEach((tag) => newTagsSet.add(tag))
        mergedFix.tags = Array.from(newTagsSet)
      } else {
        // if not we just want to add the tags to the end of the array
        const existingTagsSet = new Set(existingFix.tags)
        fix.tags.forEach((tag) => existingTagsSet.add(tag))
        mergedFix.tags = Array.from(existingTagsSet)
      }

      uniqueFixes[existingIndex] = mergedFix
    }
  })

  uniqueFixes.forEach((fix) => sortFixTags(fix))

  // Sort uniqueFixes by the first tag
  return uniqueFixes.sort((a, b) => {
    const fixTypePriority = compareByFixType(a, b)
    if (fixTypePriority !== 0) return fixTypePriority

    const aTag = a.tags[0] || ""
    const bTag = b.tags[0] || ""
    return aTag.localeCompare(bTag)
  })
}

const sortFixTags = (fix: BaseImageFix) => {
  if (
    fix.tags.length > 1 &&
    fix.currentUrl.length > 1 &&
    fix.currentUrl.includes(":")
  ) {
    const currentTag = fix.currentUrl.split(":")[1]
    let bestMatchIndex = -1
    let bestMatch = -1

    fix.tags.forEach((tag, tagIndex) => {
      if (tag.length > bestMatch) {
        const currentScore = calcMatchLength(currentTag, tag)
        if (currentScore > bestMatch) {
          bestMatch = currentScore
          bestMatchIndex = tagIndex
        }
      }
    })

    if (bestMatchIndex > 0) {
      fix.tags.unshift(...fix.tags.splice(bestMatchIndex, 1))
    }
  }
}

const calcMatchLength = (base: string, tag: string): number => {
  let matchLength = 0
  while (
    matchLength < base.length &&
    matchLength < tag.length &&
    base[matchLength] === tag[matchLength]
  ) {
    matchLength++
  }
  return matchLength
}
